// utils/db/unifiedIndexedDB.ts

import Dexie, { type Table } from 'dexie'

/**
 * 통합 IndexedDB 데이터 구조
 */
export interface StoredData<T> {
  id: string // '{companyId}_{tableName}' 형식
  companyId: string
  tableName: string
  items: T[]
  version: number
  lastSync: number
  timestamp: number
}

/**
 * 통합 IndexedDB 데이터베이스
 * 모든 스토어가 하나의 DB를 공유
 */
class UnifiedDatabase extends Dexie {
  // 통합 테이블 - 모든 스토어 데이터를 하나의 테이블에 저장
  storeData!: Table<StoredData<any>>

  constructor() {
    super('FarmSNS_Database')

    // 데이터베이스 스키마 정의
    this.version(1).stores({
      // 복합 인덱스로 효율적인 조회
      storeData: 'id, [companyId+tableName], companyId, tableName, timestamp',
    })
  }

  /**
   * 스토어별 데이터 저장
   */
  async saveStoreData<T>(
    companyId: string,
    tableName: string,
    data: {
      items: T[]
      version: number
      lastSync: number
    }
  ): Promise<void> {
    const recordId = `${companyId}_${tableName}`
    console.log(
      `🔍 IndexedDB Debug - [UnifiedDB] 저장 시작 - recordId: ${recordId}, 항목 수: ${data.items.length}`
    )

    await this.storeData.put({
      id: recordId,
      companyId,
      tableName,
      items: data.items,
      version: data.version,
      lastSync: data.lastSync,
      timestamp: Date.now(),
    })

    console.log(`🔍 IndexedDB Debug - [UnifiedDB] 저장 완료 - ${recordId}`)
  }

  /**
   * 스토어별 데이터 로드
   */
  async loadStoreData<T>(
    companyId: string,
    tableName: string
  ): Promise<{
    items: T[]
    version: number
    lastSync: number
  } | null> {
    const recordId = `${companyId}_${tableName}`
    console.log(`🔍 IndexedDB Debug - [UnifiedDB] 조회 시도 - recordId: ${recordId}`)

    const record = await this.storeData.get(recordId)
    console.log(
      `🔍 IndexedDB Debug - [UnifiedDB] 조회 결과:`,
      record ? `찾음 (${record.items.length}개 항목)` : '없음'
    )

    if (!record) return null

    return {
      items: record.items as T[],
      version: record.version,
      lastSync: record.lastSync,
    }
  }

  /**
   * 특정 회사의 특정 스토어 데이터 삭제
   */
  async clearStoreData(companyId: string, tableName: string): Promise<void> {
    const recordId = `${companyId}_${tableName}`
    await this.storeData.delete(recordId)
  }

  /**
   * 특정 회사의 모든 데이터 삭제
   */
  async clearCompanyData(companyId: string): Promise<void> {
    await this.storeData.where({ companyId }).delete()
  }

  /**
   * 특정 스토어의 모든 회사 데이터 삭제
   */
  async clearTableData(tableName: string): Promise<void> {
    await this.storeData.where({ tableName }).delete()
  }

  /**
   * 전체 데이터베이스 초기화
   */
  async clearAllData(): Promise<void> {
    await this.storeData.clear()
  }

  /**
   * 데이터베이스 통계 조회
   */
  async getStatistics(): Promise<{
    totalRecords: number
    byCompany: Record<string, number>
    byTable: Record<string, number>
    totalSizeKB: number
  }> {
    const allRecords = await this.storeData.toArray()

    const stats = {
      totalRecords: allRecords.length,
      byCompany: {} as Record<string, number>,
      byTable: {} as Record<string, number>,
      totalSizeKB: 0,
    }

    for (const record of allRecords) {
      // 회사별 카운트
      stats.byCompany[record.companyId] = (stats.byCompany[record.companyId] || 0) + 1

      // 테이블별 카운트
      stats.byTable[record.tableName] = (stats.byTable[record.tableName] || 0) + 1

      // 대략적인 크기 계산 (JSON 문자열 길이 기준)
      const recordSize = JSON.stringify(record).length / 1024
      stats.totalSizeKB += recordSize
    }

    return stats
  }
}

// 싱글톤 인스턴스
let dbInstance: UnifiedDatabase | null = null

/**
 * 통합 데이터베이스 인스턴스 가져오기
 */
export function getUnifiedDB(): UnifiedDatabase {
  if (!dbInstance) {
    dbInstance = new UnifiedDatabase()
  }
  return dbInstance
}

/**
 * 데이터베이스 연결 닫기
 */
export async function closeUnifiedDB(): Promise<void> {
  if (dbInstance) {
    dbInstance.close()
    dbInstance = null
  }
}

/**
 * IndexedDB 지원 여부 확인
 */
export function isIndexedDBSupported(): boolean {
  try {
    return typeof window !== 'undefined' && 'indexedDB' in window && window.indexedDB !== null
  } catch {
    return false
  }
}

/**
 * 브라우저 스토리지 할당량 확인
 */
export async function checkStorageQuota(): Promise<{
  usage: number
  quota: number
  percentUsed: number
  usageMB: number
  quotaMB: number
}> {
  if ('navigator' in window && 'storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate()
    const usage = estimate.usage || 0
    const quota = estimate.quota || 0
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0

    return {
      usage,
      quota,
      percentUsed,
      usageMB: usage / (1024 * 1024),
      quotaMB: quota / (1024 * 1024),
    }
  }

  return {
    usage: 0,
    quota: 0,
    percentUsed: 0,
    usageMB: 0,
    quotaMB: 0,
  }
}
